import { handleAssistirCampo } from "../api";
import { Action, DraftData } from "../types";
import { setLastEditNow } from "../lastedit";
import Quill from "quill";

let campoAtivoId: string | null = null;
let isPanelOpen = false;
let isProcessingIA = false;

const getActionSubtitle = (action?: Action): string => {
  switch (action) {
    case Action.textual_review:
      return "📝 Revisão textual";
    case Action.critical_review:
      return "💡 Revisão de conteúdo";
    case Action.suggest_new:
      return "✨ Sugestão";
    case Action.rewrite:
      return "✍️ Reescrita";
    case Action.shrink:
      return "🪄 Versão encurtada";
    default:
      return "";
  }
};

export function initAssistantPanel(): void {
  const panel = document.getElementById('assistant-panel');
  const overlay = document.getElementById('assistant-overlay');

  if (!panel || !overlay) return;

  console.log("Panel e overlay encontrados:", panel, overlay);

  const btnActions = {
    // revisar: document.getElementById('revisar-btn'), // Desativado — dividido em revisar-texto e revisar-conteudo
    revisarTexto: document.getElementById('revisar-texto-btn'),
    revisarConteudo: document.getElementById('revisar-conteudo-btn'),
    reescrever: document.getElementById('reescrever-btn'),
    sugerir: document.getElementById('sugerir-btn'),
    encurtar: document.getElementById('encurtar-btn')
  };

  console.log("Botões de ação:", btnActions);

  const contextLabel = panel.querySelector<HTMLElement>('[data-assistant-context]');
  const input = panel.querySelector<HTMLTextAreaElement>('#assistant-input');

  const chat = panel.querySelector<HTMLElement>('#assistant-chat');
  const sendBtn = panel.querySelector<HTMLButtonElement>('#assistant-send');

  let activeHeading: HTMLElement | null = null;

  const getHeadingKey = (heading: HTMLElement): string => {
    return (
      heading.getAttribute('data-help-key') ||
      heading.id ||
      `heading-${Array.from(document.querySelectorAll('[data-help-key], h2, h3')).indexOf(heading)}`
    );
  };

  const ensureInlineResponseSlot = (heading: HTMLElement, sectionTitle: string) => {
    const key = getHeadingKey(heading);
    const existing = document.querySelector<HTMLElement>(
      `.assistant-inline-response[data-assistant-for="${CSS.escape(key)}"]`
    );

    if (existing) {
      existing.style.display = 'block';
      return existing;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'assistant-inline-response';
    wrapper.dataset.assistantFor = key;
    wrapper.innerHTML = `
  <div class="assistant-inline-response__header">
    <span class="assistant-inline-response__icon">🤖</span>
    <div class="assistant-inline-response__title">
      Resposta da IA — <strong>${sectionTitle}</strong>
    </div>
  </div>

  <div class="assistant-inline-response__action" data-assistant-inline-action>
  </div>

  <div class="assistant-inline-response__body" data-assistant-inline-body>
    Envie uma mensagem no painel para gerar uma resposta aqui.
  </div>
`;
    wrapper.style.display = 'block';
    // Add "Usar Texto" button to copy response into the associated editor
    const useBtn = document.createElement('button');
    useBtn.type = 'button';
    useBtn.className = 'use-text-btn';
    useBtn.textContent = 'Usar Texto';
    useBtn.disabled = true;
    const headerEl = wrapper.querySelector('.assistant-inline-response__header');
    headerEl?.appendChild(useBtn);
    useBtn.addEventListener('click', () => {
      const bodyEl = wrapper.querySelector('[data-assistant-inline-body]');
      const responseText = bodyEl?.innerHTML ?? '';
      if (campoAtivoId) {
        atualizarCampoComIA(campoAtivoId, responseText);
      }
    });

    let cursor = heading.nextElementSibling as HTMLElement | null;
    let lastInsideThisSection: HTMLElement = heading;

    while (cursor) {
      const tag = cursor.tagName.toLowerCase();
      if (tag === 'h2' || tag === 'h3' || tag === 'section') break;
      lastInsideThisSection = cursor;
      cursor = cursor.nextElementSibling as HTMLElement | null;
    }

    lastInsideThisSection.insertAdjacentElement('afterend', wrapper);

    return wrapper;
  };

  const hideAllInlineResponses = () => {
    document
      .querySelectorAll<HTMLElement>('.assistant-inline-response')
      .forEach((el) => {
        el.style.display = 'none';
      });
  };

  const setInlineResponseText = (text: string, action?: Action) => {
    if (!activeHeading) return;

    const title = activeHeading.dataset.assistantTitle || activeHeading.textContent?.trim() || 'esta seção';
    const slot = ensureInlineResponseSlot(activeHeading, title);
    const body = slot.querySelector<HTMLElement>('[data-assistant-inline-body]');
    const actionLabel = slot.querySelector<HTMLElement>('[data-assistant-inline-action]');

    if (actionLabel) {
      actionLabel.textContent = getActionSubtitle(action);
    }

    if (body) {
      body.innerHTML = text;

      const useBtn = slot.querySelector<HTMLButtonElement>('.use-text-btn');
      if (useBtn) {
        const trimmedText = text.trim();
        useBtn.disabled = trimmedText === "Envie uma mensagem no painel para gerar uma resposta aqui." || 
                          trimmedText === "Processando solicitação com a IA...";
      }

      requestAnimationFrame(() => {
        slot.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
  };

  const appendChatMessage = (role: 'user' | 'assistant', text: string) => {
    if (!chat) return;
    const bubble = document.createElement('div');
    bubble.className = `assistant-msg assistant-msg--${role}`;
    bubble.textContent = text;
    chat.appendChild(bubble);
    chat.scrollTop = chat.scrollHeight;
  };

  const getDefaultAssistantReply = (sectionTitle: string, userText: string) => {
    return `Ok — entendi sua dúvida em “${sectionTitle}”.\n\nVocê escreveu: “${userText}”\n\nAguardando resposta da IA...`;
  };

  const openPanel = (sectionTitle: string, targetFieldId: string, heading?: HTMLElement): void => {
    console.log("Abrindo painel da IA para a seção:", sectionTitle);  // Log de abertura do painel
    campoAtivoId = targetFieldId;
    if (contextLabel) contextLabel.textContent = sectionTitle;

    if (heading) {
      hideAllInlineResponses();

      activeHeading = heading;

      const slot = ensureInlineResponseSlot(heading, sectionTitle);
      slot.style.display = 'block';
    }

    panel.classList.add('assistant-panel--open');
    overlay.classList.add('assistant-overlay--visible');
    panel.setAttribute('aria-hidden', 'false');
    isPanelOpen = true;
    document.body.classList.add('assistant-open');

    if (input) setTimeout(() => input.focus(), 150);
  };

  async function executarAcaoIA(action: Action) {
    if (!campoAtivoId || isProcessingIA) return;
    
    // Salvar o campo e o heading atual localmente para o caso do usuário navegar para outra seção
    const idAtual = campoAtivoId;
    const headingAtual = activeHeading;

    const actionsContainer = panel?.querySelector('.assistant-panel-actions');
    const actionBtns = panel?.querySelectorAll<HTMLButtonElement>('.assistant-action-btn');

    console.log("Ação IA disparada:", action);

    // Função auxiliar local para garantir que adicionamos o texto no lugar certo
    const setTargetInlineText = (text: string, act?: Action) => {
      if (!headingAtual) return;
      const title = headingAtual.dataset.assistantTitle || headingAtual.textContent?.trim() || 'esta seção';
      const slot = ensureInlineResponseSlot(headingAtual, title);
      const body = slot.querySelector<HTMLElement>('[data-assistant-inline-body]');
      const actionLabel = slot.querySelector<HTMLElement>('[data-assistant-inline-action]');

      if (actionLabel) {
        actionLabel.textContent = getActionSubtitle(act);
      }

      if (body) {
        body.innerHTML = text;

        const useBtn = slot.querySelector<HTMLButtonElement>('.use-text-btn');
        if (useBtn) {
          const trimmedText = text.trim();
          useBtn.disabled = trimmedText === "Envie uma mensagem no painel para gerar uma resposta aqui." || 
                            trimmedText === "Processando solicitação com a IA...";
        }

        requestAnimationFrame(() => {
          slot.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      }
    };

    try {
      isProcessingIA = true;
      actionsContainer?.classList.add('loading');
      actionBtns?.forEach(btn => btn.disabled = true);

      console.log("Enviando requisição para IA...");

      setTargetInlineText("Processando solicitação com a IA...", action);

      const novoTexto = await handleAssistirCampo(action, idAtual as keyof DraftData);

      if (novoTexto) {
        console.log("Resposta da IA recebida:", novoTexto);
        setTargetInlineText(novoTexto, action);
        setLastEditNow();
      } else {
        console.log("Nenhuma resposta da IA recebida");
      }

    } catch (error) {
      console.error("Erro ao executar ação IA:", error);
      alert("O Assis-IA encontrou um problema ao processar sua solicitação.");
    } finally {
      isProcessingIA = false;
      actionsContainer?.classList.remove('loading');
      actionBtns?.forEach(btn => btn.disabled = false);
    }
  }

  // Botão "Revisar" original desativado — dividido em duas ações
  // btnActions.revisar?.addEventListener('click', () => {
  //   console.log("Botão Revisar clicado");
  //   executarAcaoIA(Action.critical_review);
  // });
  btnActions.revisarTexto?.addEventListener('click', () => {
    console.log("Botão Revisar texto clicado");
    executarAcaoIA(Action.textual_review);
  });
  btnActions.revisarConteudo?.addEventListener('click', () => {
    console.log("Botão Revisar conteúdo clicado");
    executarAcaoIA(Action.critical_review);
  });
  btnActions.reescrever?.addEventListener('click', () => {
    console.log("Botão Reescrever clicado");
    executarAcaoIA(Action.rewrite);
  });
  btnActions.sugerir?.addEventListener('click', () => {
    console.log("Botão Sugerir clicado");
    executarAcaoIA(Action.suggest_new);
  });
  btnActions.encurtar?.addEventListener('click', () => {
    console.log("Botão Encurtar clicado");
    executarAcaoIA(Action.shrink);
  });

  panel.querySelector('[data-assistant-close]')?.addEventListener('click', closePanel);
  overlay.addEventListener('click', closePanel);

  const updateSendState = () => {
    if (!input || !sendBtn) return;
    sendBtn.disabled = (input.value || '').trim().length === 0;
  };

  const handleSend = () => {
    if (!input || isProcessingIA) return;
    const userText = (input.value || '').trim();
    if (!userText) return;

    const sectionTitle = activeHeading?.dataset.assistantTitle || contextLabel?.textContent || 'esta seção';

    appendChatMessage('user', userText);

    const reply = getDefaultAssistantReply(sectionTitle, userText);
    setInlineResponseText(reply, Action.suggest_new);

    // Chama a função para enviar os dados para a IA
    console.log("Enviando dados para a IA...");
    executarAcaoIA(Action.suggest_new);

    requestAnimationFrame(() => {
      const scroll = document.querySelector<HTMLElement>('.assistant-panel-scroll');
      if (scroll) {
        scroll.scrollTop = scroll.scrollHeight;
      }
    });

    input.value = '';
    updateSendState();
    input.focus();
  };

  input?.addEventListener('input', updateSendState);
  sendBtn?.addEventListener('click', handleSend);

  input?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  });

  const headings = document.querySelectorAll<HTMLElement>('[data-assistant="true"]');
  headings.forEach((heading) => {
    const targetId = descobrirIdDoCampo(heading);
    if (heading.querySelector('.assistant-trigger')) return;

    const rawTitle = heading.textContent?.trim() ?? '';
    heading.dataset.assistantTitle = rawTitle;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'assistant-trigger';
    btn.title = 'Pedir ajuda ao Assis-IA para esta seção';
    btn.innerHTML = `
      <span class="assistant-trigger-icon">🤖</span>
      <span class="assistant-trigger-text">IA</span>
    `;

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      console.log("Botão de IA clicado para a seção:", targetId);
      const sectionTitle = heading.dataset.assistantTitle || 'esta seção';
      openPanel(sectionTitle, targetId, heading);
      requestAnimationFrame(() => scrollToHeading(heading));
    });

    heading.classList.add('has-assistant-trigger');
    heading.appendChild(btn);
  });

  // ── Troca automática de contexto ao interagir com campos editáveis de outra seção ──
  //
  // Monta um mapa: fieldId (ou sentinela para sec-resultados) → heading correspondente.
  // Para cada heading com data-assistant="true", localiza os elementos editáveis associados
  // e registra listeners. Se o painel estiver aberto e o campo clicado pertencer a uma
  // seção diferente da atual, o contexto do painel é atualizado.

  const helpKeyToHeading = new Map<string, HTMLElement>();
  headings.forEach((h) => {
    const key = h.getAttribute('data-help-key');
    if (key) helpKeyToHeading.set(key, h);
  });

  const scrollToHeading = (heading: HTMLElement) => {
    let target: HTMLElement = heading;
    if (heading.tagName.toLowerCase() === 'h3') {
      const card = heading.closest('.card');
      if (card) {
        target = card as HTMLElement;
      }
    }
    const topMargin = 32; 
    const targetPosition = target.getBoundingClientRect().top + window.scrollY - topMargin;
    window.scrollTo({
      top: targetPosition,
      behavior: 'smooth'
    });
  };

  // Seções cujo campo editável é um .ql-editor dentro do container quill-editor
  const qlSections: Array<{ fieldId: string; helpKey: string }> = [
    { fieldId: 'objetivosGerais',          helpKey: 'sec-obj-gerais' },
    { fieldId: 'objetivosEspecificos',     helpKey: 'sec-obj-especificos' },
    { fieldId: 'justificativa',            helpKey: 'sec-justificativa' },
    { fieldId: 'responsabilidadesUFC',     helpKey: 'sec-desc-atividades-ufc' },
    { fieldId: 'responsabilidadesParceiro',helpKey: 'sec-desc-atividades-parceiro' },
    { fieldId: 'atividadesConjuntas',      helpKey: 'sec-desc-atividades-conjuntas' },
    { fieldId: 'direitosPI',               helpKey: 'sec-direitos-pi' },
    { fieldId: 'recursosFinanceiros',      helpKey: 'sec-meios-financeiros' },
    { fieldId: 'infraestrutura',           helpKey: 'sec-meios-infra' },
  ];

  qlSections.forEach(({ fieldId, helpKey }) => {
    const container = document.getElementById(fieldId);
    if (!container) return;

    // Handler compartilhado — atualiza o contexto do painel para esta seção.
    // Acionado por três eventos:
    //   • mousedown → clique do mouse no .ql-editor OU na toolbar do Quill
    //                 (a toolbar div[role="toolbar"] é filha do container e o evento
    //                  borbulha até aqui automaticamente).
    //   • focusin   → navegação via teclado (Tab) para dentro do .ql-editor.
    //   • input     → digitação dentro do .ql-editor (ex.: após foco via Tab).
    const switchToSection = (e?: Event) => {
      const heading = helpKeyToHeading.get(helpKey);
      
      const shouldScroll = heading && e && e.type === 'mousedown';

      if (!isPanelOpen) {
        if (shouldScroll) scrollToHeading(heading!);
        return;
      }
      if (campoAtivoId === fieldId) {
        if (shouldScroll) scrollToHeading(heading!);
        return;
      }

      if (!heading) return;

      const sectionTitle = heading.dataset.assistantTitle || heading.textContent?.trim() || 'esta seção';
      openPanel(sectionTitle, fieldId, heading);

      if (shouldScroll) {
        requestAnimationFrame(() => scrollToHeading(heading!));
      }
    };

    container.addEventListener('mousedown', switchToSection);
    container.addEventListener('focusin',   switchToSection);
    container.addEventListener('input',     switchToSection);
  });

  // Seção 7 — Resultados Esperados (chave: sec-resultados)
  // Elementos editáveis: botão #add-atividade, textarea .input-meta, textarea .input-indicador.
  // As linhas são renderizadas dinamicamente, por isso usamos delegação de eventos no container.
  // (A seção 7 não possui toolbar do Quill.)
  const sec7HelpKey = 'sec-resultados';
  const sec7FieldId = 'atividades'; // id utilizado em campoAtivoId para esta seção

  const switchToSec7 = (e?: Event) => {
    const heading = helpKeyToHeading.get(sec7HelpKey);

    const shouldScroll = heading && e && e.type === 'mousedown';

    if (!isPanelOpen) {
      if (shouldScroll) scrollToHeading(heading!);
      return;
    }
    if (campoAtivoId === sec7FieldId) {
      if (shouldScroll) scrollToHeading(heading!);
      return;
    }

    if (!heading) return;

    const sectionTitle = heading.dataset.assistantTitle || heading.textContent?.trim() || 'Resultados Esperados';
    openPanel(sectionTitle, sec7FieldId, heading);

    if (shouldScroll) {
      requestAnimationFrame(() => scrollToHeading(heading!));
    }
  };

  // Botão estático #add-atividade (apenas mousedown — não é um campo de texto)
  document.getElementById('add-atividade')?.addEventListener('mousedown', switchToSec7);

  // Linhas dinâmicas — delega mousedown, focusin e input no container #atividades-lista
  const atividadesLista = document.getElementById('atividades-lista');

  const isEditableRow = (el: HTMLElement) =>
    el.classList.contains('input-meta') || el.classList.contains('input-indicador');

  atividadesLista?.addEventListener('mousedown', (e) => {
    if (isEditableRow(e.target as HTMLElement)) switchToSec7(e);
  });
  atividadesLista?.addEventListener('focusin', (e) => {
    if (isEditableRow(e.target as HTMLElement)) switchToSec7(e);
  });
  atividadesLista?.addEventListener('input', (e) => {
    if (isEditableRow(e.target as HTMLElement)) switchToSec7(e);
  });

  function closePanel() {
    if (panel?.contains(document.activeElement)) {
      (document.activeElement as HTMLElement)?.blur();
    }

    panel?.classList.remove('assistant-panel--open');
    overlay?.classList.remove('assistant-overlay--visible');
    panel?.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('assistant-open');
    isPanelOpen = false;

    hideAllInlineResponses();
  }
}

function descobrirIdDoCampo(heading: HTMLElement): string {
  // 1. Se o título tiver um data-help-key específico que mapeie para o ID do campo
  const helpKey = heading.getAttribute('data-help-key');

  const mapa: Record<string, string> = {
    'sec-obj-gerais': 'objetivosGerais',
    'sec-obj-especificos': 'objetivosEspecificos',
    'sec-justificativa': 'justificativa',
    'sec-desc-atividades-ufc': 'responsabilidadesUFC',
    'sec-desc-atividades-parceiro': 'responsabilidadesParceiro',
    'sec-desc-atividades-conjuntas': 'atividadesConjuntas',
    'sec-direitos-pi': 'direitosPI',
    'sec-meios-financeiros': 'recursosFinanceiros',
    'sec-meios-infra': 'infraestrutura'
  };

  if (helpKey && mapa[helpKey]) return mapa[helpKey];

  const card = heading.nextElementSibling;
  const input = card?.querySelector('input, textarea, .quill-editor');
  return input?.id || '';
}

/**
 * Função para atualizar o valor, detectando se é Quill ou Input comum
 */
function atualizarCampoComIA(id: string, texto: string, setResponseText?: (t: string) => void) {
  setResponseText?.(texto); // Atualiza o slot de resposta inline, se aplicável
  const elemento = document.getElementById(id);
  if (!elemento) return;

  if (elemento.classList.contains('quill-editor')) {
    const editor = Quill.find(elemento);
    if (editor instanceof Quill) {
      editor.root.innerHTML = texto;
      return;
    }
  }

  if (elemento instanceof HTMLInputElement || elemento instanceof HTMLTextAreaElement) {
    elemento.value = texto;
  }
}